import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from './BlogLayout';
import { postBySlug } from './posts';

const post = postBySlug('pdf-compression-lossless-vs-lossy');

const PostPdfCompression = () => (
    <BlogLayout post={post}>
        <p>
            Two PDF compressors, same 30&nbsp;MB input file. One hands back a 28&nbsp;MB output.
            The other returns 3&nbsp;MB. They can&rsquo;t both be &quot;just compressing&quot; —
            and they&rsquo;re not. One of them is <em>lossless</em> and the other is
            <em>&nbsp;lossy</em>. Before you use either, you need to know which is which, because
            only one of them preserves the document you started with.
        </p>

        <h2>What&rsquo;s actually inside a PDF</h2>
        <p>
            A PDF is a container. It can hold:
        </p>
        <ul>
            <li><strong>Text and fonts</strong>, stored as vectors and glyph maps.</li>
            <li><strong>Images</strong>, usually as embedded JPEG, PNG, or raw raster data.</li>
            <li><strong>Vector graphics</strong>, like diagrams drawn with lines and curves.</li>
            <li><strong>Fonts</strong>, sometimes embedded as full files, sometimes as subsets.</li>
            <li><strong>Metadata</strong>, JavaScript, forms, annotations, attachments, digital signatures.</li>
            <li>
                <strong>Structural overhead</strong> — unreferenced objects, duplicated streams,
                and leftover data from edits.
            </li>
        </ul>
        <p>
            In typical documents, the file size breakdown is heavily dominated by one or two of
            these categories. A scanned contract is mostly embedded images. An annual report is
            often a mix of images, fonts, and vector charts. A plain text PDF generated from
            Word is almost entirely fonts and text streams, with surprising overhead from
            embedded fonts. Understanding your file tells you which compressor will help.
        </p>

        <h2>Lossless compression: what it touches, what it doesn&rsquo;t</h2>
        <p>
            Lossless PDF compression works on two levers: it rewrites the PDF&rsquo;s internal
            structure, and it applies flate (zlib) compression to any uncompressed streams.
            Specifically:
        </p>
        <ul>
            <li>
                <strong>Deduplicates streams.</strong> If two pages use the same image or font,
                it stores it once and references it twice.
            </li>
            <li>
                <strong>Drops unreferenced objects.</strong> PDFs that have been edited several
                times accumulate orphans — objects nothing points to anymore.
            </li>
            <li>
                <strong>Subsets fonts.</strong> If your document uses only 50 glyphs from a
                5,000-glyph font, it can embed only the 50.
            </li>
            <li>
                <strong>Compresses streams.</strong> Any object that wasn&rsquo;t already
                flate-compressed gets compressed.
            </li>
        </ul>
        <p>
            Nothing about the visible content changes. Text stays crisp. Images stay at the
            original resolution and encoding. If you diff the rendered output of the original
            and the compressed PDF, you shouldn&rsquo;t be able to tell them apart. The trade-off
            is that savings are modest. For a PDF generated cleanly by Word, you might see a 5
            to 15% reduction. For a merged, heavily-edited PDF with lots of redundant
            structure, you might see 30 to 50%. You will not see 90%.
        </p>

        <h2>Lossy compression: the big savings come with real costs</h2>
        <p>
            Lossy compressors go further — they rewrite the images. The usual moves are:
        </p>
        <ul>
            <li>
                <strong>Downsampling.</strong> A 600 DPI embedded image is resampled to 150 DPI
                because you probably don&rsquo;t need that much detail for screen viewing.
            </li>
            <li>
                <strong>Recompression.</strong> A lossless PNG inside the PDF is re-encoded as a
                lower-quality JPEG.
            </li>
            <li>
                <strong>Colour conversion.</strong> A 24-bit RGB image is downgraded to 8-bit
                indexed colour or grayscale.
            </li>
        </ul>
        <p>
            For a 30&nbsp;MB scanned contract, this is the difference between 28&nbsp;MB and
            3&nbsp;MB. The catch: once you lose pixel detail, you cannot recover it. Small
            handwritten notes blur. Signatures pixelate. Scanned IDs that were readable become
            unreadable. If the person on the other end rejects your submission because the scan
            quality is too low, you&rsquo;ll need the original.
        </p>

        <h2>Which one should you use when?</h2>

        <h3>Use lossless compression when:</h3>
        <ul>
            <li>The PDF contains legal, medical, or financial records.</li>
            <li>You&rsquo;re not sure whether the receiver will print it.</li>
            <li>The file was produced from Word, Google Docs, or similar — it&rsquo;s mostly text.</li>
            <li>You&rsquo;ve been asked to &quot;make the file smaller&quot; but quality matters.</li>
        </ul>

        <h3>Use lossy compression when:</h3>
        <ul>
            <li>The PDF is mostly scanned images and size reduction is critical.</li>
            <li>The receiver will only view it on screen, not print or archive it.</li>
            <li>You have the originals and can regenerate a higher-quality copy if needed.</li>
            <li>You&rsquo;re hitting a hard attachment cap (email, contact form upload limit).</li>
        </ul>

        <h2>Tactical tips</h2>

        <h3>Check the file composition before you compress</h3>
        <p>
            Open the PDF in a viewer that shows page dimensions and embedded font info. Adobe
            Acrobat&rsquo;s &quot;Document Properties&quot; panel shows file composition. If
            you see large embedded font subsets on a short document, that&rsquo;s wasted space.
            If you see huge image streams, lossy compression will help.
        </p>

        <h3>Try lossless first</h3>
        <p>
            It&rsquo;s free, safe, and often enough. Our{' '}
            <Link to="/compress-pdf">compress PDF tool</Link> runs a lossless pass in your
            browser — no file uploads, no quality loss, and the reduction percentage is shown
            immediately. If lossless gets you under your limit, you&rsquo;re done.
        </p>

        <h3>Only escalate to lossy if necessary</h3>
        <p>
            If lossless isn&rsquo;t enough, then reach for a desktop tool like Adobe Acrobat Pro
            or a command-line utility like Ghostscript. Both offer fine control: preset
            profiles for screen, ebook, printer, and prepress quality. Adjust and re-test
            rather than blindly applying the most aggressive setting.
        </p>

        <h3>Split instead of squeeze</h3>
        <p>
            For email especially, splitting a long PDF into parts often works better than
            compressing it. A 50-page report split into two 25-page PDFs passes most attachment
            filters without any quality loss. Desktop PDF tools have built-in split commands.
        </p>

        <h3>Link, don&rsquo;t attach</h3>
        <p>
            For very large files, upload to cloud storage (Google Drive, Dropbox, OneDrive) and
            share a link. This avoids the compression-vs-quality trade-off entirely and is often
            more professional than sending a 50&nbsp;MB attachment.
        </p>

        <h2>Why browser-based compression is worth considering</h2>
        <p>
            Uploading a compressed PDF to a remote compressor feels silly when you think about
            it: you&rsquo;re uploading the file to reduce its size. A browser-based lossless
            compressor like ours runs entirely on your device, so there&rsquo;s no upload, no
            retention risk, and the file never sits on someone else&rsquo;s server. The
            trade-off is that browser engines can&rsquo;t do the heaviest lossy operations as
            efficiently as native tools — but for lossless work, they&rsquo;re competitive and
            a better privacy choice.
        </p>

        <h2>Summary</h2>
        <p>
            Lossless compression restructures the file. Lossy compression rewrites its content.
            One preserves your document exactly; the other trades quality for size. If you
            understand which problem you&rsquo;re solving, you&rsquo;ll pick the right tool
            every time — and you won&rsquo;t accidentally ship a blurry signed contract because
            you hit &quot;maximum compression.&quot;
        </p>
    </BlogLayout>
);

export default PostPdfCompression;
